<template>
  <ContainerInfoItem
    source="deal_revenue_graph"
    title="Revenue by month"
    :description="range.label"
    height="fit-content"
  >
    <SkeletonLoader v-if="isLoading" :page="loaderPages.CHART" />

    <BarChart
      v-else-if="chartCategories && chartData"
      id="deal_revenue_graph"
      primary-color="#48AF7D"
      :categories="chartCategories"
      :series-data="chartData"
      :custom-tooltip-formatter="(value) => `${primaryCurrencySymbol}${value}`"
      :show-currency="true"
      :currency-symbol="primaryCurrencySymbol"
    />

    <EmptyScreen
      v-else
      source="deal_revenue_graph"
      text="No data found!"
      :type="emptyScreenTypes.CHART_AND_TABLE"
    />
  </ContainerInfoItem>
</template>

<script setup>
import { computed, onMounted, ref, watchEffect } from "vue";
import { loaderPages } from "@/src/constants/loaderConstants.js";
import { emptyScreenTypes } from "@/src/constants/searchConstants.js";
import { getMonthName } from "@/src/utils/dateTimeUtils.js";
import store from "@/src/store/index.js";
import AnalyticService from "@/src/api/AnalyticService.js";
import EmptyScreen from "@/src/components/EmptyScreen.vue";
import BarChart from "@/src/components/charts/BarChart.vue";
import SkeletonLoader from "@/src/components/SkeletonLoader.vue";
import ContainerInfoItem from "@/src/components/info_components/ContainerInfoItem.vue";
import { useAnalyticsRefresh } from "@/src/composables/useAnalyticsRefresh.js";

const props = defineProps({
  primaryCurrencySymbol: { type: String, default: "$" },
});

const range = computed(() => store.getters["analytics/getRange"]);
const filters = computed(() => store.getters["analytics/getFilters"]);
const userIds = computed(() => store.getters["analytics/getUserIds"]);
const teamIds = computed(() => store.getters["analytics/getTeamIds"]);

const chartCategories = ref(null);
const chartData = ref(null);
const isLoading = ref(false);

const toFetchDealRevenueMonthly = async () => {
  try {
    isLoading.value = true;

    const params = {
      deal_owner_id: userIds.value,
      team_id: teamIds.value,
      pipeline_id: filters.value?.pipeline?.id,
      start_time: range.value?.start,
      end_time: range.value?.end,
    };

    const response = await AnalyticService.V2.fetchDealRevenueMonthly(params);
    if (response?.success) {
      const data = response?.data?.monthly_revenue;

      if (!data || data.length === 0) {
        chartCategories.value = null;
        chartData.value = null;
        return;
      }

      const sortedData = [...data].sort((a, b) => {
        if (a.year !== b.year) return a.year - b.year;
        return a.month - b.month;
      });

      const firstEntry = sortedData[0];
      const startYear = firstEntry.year;
      const startMonth = firstEntry.month;

      const revenueMap = {};
      data.forEach((item) => {
        const key = `${item.year}-${item.month}`;
        revenueMap[key] = item.revenue;
      });

      const monthlyData = [];
      let currentYear = startYear;
      let currentMonth = startMonth;

      for (let i = 0; i < 12; i++) {
        const key = `${currentYear}-${currentMonth}`;
        const revenue = revenueMap[key] || 0;

        monthlyData.push({
          year: currentYear,
          month: currentMonth,
          revenue: revenue,
          label: `${getMonthName(currentMonth, currentYear)} ${currentYear}`,
        });

        currentMonth++;
        if (currentMonth > 12) {
          currentMonth = 1;
          currentYear++;
        }
      }

      chartCategories.value = monthlyData.map((item) => item.label);
      chartData.value = [
        {
          name: "Revenue",
          data: monthlyData.map((item) => item.revenue),
        },
      ];
    }
  } catch (error) {
    console.error(
      "An error occurred while fetching the deal revenue monthly: ",
      error,
    );
  } finally {
    isLoading.value = false;
  }
};

watchEffect(() => {
  if (range.value && userIds.value && teamIds.value)
    toFetchDealRevenueMonthly();
});

useAnalyticsRefresh(toFetchDealRevenueMonthly, [range, userIds, teamIds]);
</script>
